import { useInfiniteQuery, UseInfiniteQueryOptions, useQuery, UseQueryOptions } from 'react-query'
import { OfferingsApi as LegacyOfferingsApi } from '@trybeapp/sdk.shop'
import { useCurrentSiteId } from 'contexts/SiteConfig'
import {
  ListOfferingsRequest,
  ListOfferingsResponse,
  OfferingsApi,
  ResponseError,
} from '@trybeapp/sdk'
import { apiConfiguration } from 'utilities/TrybeApiClient'
import { SetOptional } from 'type-fest'
import { ApiResponse, LegacySdkResponseError } from 'api/api.types'

const legacyApi = new LegacyOfferingsApi()
const api = new OfferingsApi(apiConfiguration)

export const useOfferingSearch = (
  params: Omit<ListOfferingsRequest, 'siteId'> = {},
  options: UseQueryOptions<ListOfferingsResponse, ResponseError> = {}
) => {
  const siteId = useCurrentSiteId()

  return useQuery<ListOfferingsResponse, ResponseError>(
    ['offeringSearch', siteId, params],
    () => api.listOfferings({ ...params, siteId }),
    {
      cacheTime: 60 * 60 * 1000, // 1 hour
      staleTime: 5 * 60 * 1000, // 5 minutes,
      ...options,
    }
  )
}

interface InfiniteOfferingsResponse {
  data: ListOfferingsResponse['data']
  nextPage: number | undefined
  page: number
  total: number
}

export const useInfiniteOfferings = (
  params: SetOptional<Omit<ListOfferingsRequest, 'page'>, 'siteId'> = {},
  options: UseInfiniteQueryOptions<InfiniteOfferingsResponse, ResponseError> = {}
) => {
  const siteId = useCurrentSiteId()

  return useInfiniteQuery<InfiniteOfferingsResponse, ResponseError>(
    ['offeringsSearch', { siteId, ...params }],
    async ({ pageParam: page = 1 }) => {
      const response = await api.listOfferings({ siteId, ...params, page })

      return {
        data: response.data,
        page,
        nextPage: response.meta.last_page > page ? page + 1 : undefined,
        total: response.meta.total,
      }
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextPage,
      cacheTime: 60 * 60 * 1000, // 1 hour
      staleTime: 5 * 60 * 1000, // 5 minutes
      ...options,
    }
  )
}

export const useGetMostPopularOfferings = (
  params: Record<string, any> = {},
  options: UseQueryOptions<ApiResponse<any>, LegacySdkResponseError> = {}
) => {
  const siteId = useCurrentSiteId()

  return useQuery<ApiResponse<any>, LegacySdkResponseError>(
    ['mostPopularOfferings', siteId, params],
    () => legacyApi.getMostPopularOfferings(siteId, params),
    {
      ...options,
      cacheTime: 24 * 60 * 60 * 1000, // 24 hours
      staleTime: 24 * 60 * 60 * 1000, // 24 hours
    }
  )
}
